import { useState, useMemo } from 'react'
import type { MaterialStockOverviewItem } from '@/services/inventory/types'
import { ShieldAlert, Package } from 'lucide-react'

export interface StockMobileCardListProps {
  items: MaterialStockOverviewItem[]
  onOpenHoldModal: (m: MaterialStockOverviewItem) => void
  onOpenProperties: (materialId: string) => void
  isBelowThreshold: (m: MaterialStockOverviewItem) => boolean
  stockDisplayName: (m: MaterialStockOverviewItem) => string
  canEdit: boolean
  mode?: 'edit' | 'count' | null
  countedQuantities?: Record<string, number>
  onCountChange?: (materialId: string, val: string) => void
}

export function StockMobileCardList({
  items,
  onOpenHoldModal,
  onOpenProperties,
  isBelowThreshold,
  stockDisplayName,
  mode,
  countedQuantities = {},
  onCountChange,
}: StockMobileCardListProps) {
  const [displayCount, setDisplayCount] = useState(25)

  const visibleItems = useMemo(() => items.slice(0, displayCount), [items, displayCount])

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-200 m-2">
        <Package className="h-10 w-10 text-slate-300 mb-2" strokeWidth={1.5} />
        <p className="text-xs font-medium">Không tìm thấy vật tư phù hợp</p>
        <p className="text-[11px] text-slate-400 mt-0.5">Thử đổi từ khóa tìm kiếm hoặc bỏ chọn bộ lọc</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2.5 p-2 overflow-y-auto min-h-0">
      {visibleItems.map((m) => {
        const below = isBelowThreshold(m)
        const physical = m.physical_available ?? m.available ?? 0
        const reserved = m.reserved_base ?? 0
        const availableToUse = Math.max(0, physical - reserved)
        const incoming = m.incoming_qty ?? m.incoming_base ?? 0

        return (
          <div
            key={m.material_id}
            className={`rounded-lg border bg-white p-3 shadow-2xs transition ${
              below ? 'border-red-300 ring-1 ring-red-200/50 bg-red-50/15' : 'border-slate-200'
            }`}
          >
            {/* Header: Code & Health badge */}
            <div className="flex items-start justify-between gap-2 border-b border-slate-100 pb-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-mono text-[11px] font-bold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">
                    {m.code || '—'}
                  </span>
                  {m.is_free_stock && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                      Tự do
                    </span>
                  )}
                  {below && (
                    <span className="inline-flex items-center gap-0.5 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">
                      <ShieldAlert className="h-3 w-3" />
                      Dưới ngưỡng
                    </span>
                  )}
                </div>

                <h3
                  className={`mt-1 text-xs leading-snug line-clamp-2 ${
                    below ? 'text-red-700 font-bold' : 'text-slate-900 font-semibold'
                  }`}
                  title={stockDisplayName(m)}
                >
                  {stockDisplayName(m)}
                </h3>
              </div>

              <span className="shrink-0 rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                {m.uom_default || '—'}
              </span>
            </div>

            {/* Metrics 4-grid */}
            <div className="grid grid-cols-2 gap-2 py-2.5">
              {/* Tồn thực tế */}
              <div className="rounded-md bg-slate-50 p-2 border border-slate-100">
                <div className="text-[11px] text-slate-500">Tồn thực tế</div>
                <div className="text-sm font-bold text-slate-800 font-mono">
                  {physical.toLocaleString('vi-VN')}
                </div>
              </div>

              {/* Đã giữ - Clickable for Hold */}
              <div
                onClick={() => onOpenHoldModal(m)}
                role="button"
                tabIndex={0}
                className="cursor-pointer rounded-md bg-blue-50/70 p-2 border border-blue-200 hover:bg-blue-100/70 active:bg-blue-200/80 transition"
              >
                <div className="flex items-center justify-between text-[11px] font-medium text-blue-700">
                  <span>Đã giữ</span>
                  <span className="text-[10px] text-blue-600 underline">Chi tiết ›</span>
                </div>
                <div className="text-sm font-bold text-blue-900 font-mono">
                  {reserved.toLocaleString('vi-VN')}
                </div>
              </div>

              {/* Có thể dùng */}
              <div
                className={`rounded-md p-2 border ${
                  below ? 'bg-red-50/60 border-red-200' : 'bg-emerald-50/60 border-emerald-200'
                }`}
              >
                <div className={`text-[11px] font-medium ${below ? 'text-red-700' : 'text-emerald-700'}`}>
                  Có thể dùng
                </div>
                <div
                  className={`text-sm font-bold font-mono ${below ? 'text-red-700' : 'text-emerald-800'}`}
                >
                  {availableToUse.toLocaleString('vi-VN')}
                </div>
              </div>

              {/* Đang về */}
              <div className="rounded-md bg-slate-50 p-2 border border-slate-100">
                <div className="text-[11px] text-slate-500">Đang về</div>
                <div className="text-sm font-bold text-slate-800 font-mono">
                  {incoming.toLocaleString('vi-VN')}
                </div>
              </div>
            </div>

            {/* If in stocktake count mode: show counted input */}
            {mode === 'count' && !m.is_free_stock && (
              <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                <label className="text-[11px] font-bold text-amber-900 block mb-1">
                  Nhập số lượng kiểm kê:
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={countedQuantities[m.material_id] ?? ''}
                  onChange={(e) => onCountChange?.(m.material_id, e.target.value)}
                  placeholder={`Số đếm (HT: ${physical})`}
                  className="w-full border border-amber-300 rounded px-2.5 py-1 text-xs bg-white font-mono"
                />
              </div>
            )}

            {/* Actions Bar */}
            <div className="flex items-center gap-2 border-t border-slate-100 pt-2">
              <button
                type="button"
                onClick={() => onOpenProperties(m.material_id)}
                className="flex-1 rounded-md border border-slate-200 bg-white py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 text-center"
              >
                Xem chi tiết
              </button>
              <button
                type="button"
                onClick={() => onOpenHoldModal(m)}
                className="flex-1 rounded-md border border-blue-600 bg-blue-600 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 active:bg-blue-800 text-center shadow-2xs"
              >
                ⚡ Giữ hàng
              </button>
            </div>
          </div>
        )
      })}

      {/* Load More Button */}
      {items.length > displayCount && (
        <button
          type="button"
          onClick={() => setDisplayCount((c) => c + 30)}
          className="w-full py-2.5 my-1 bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-700 text-xs font-semibold rounded-md border border-slate-200 transition text-center"
        >
          Xem thêm ({items.length - displayCount} vật tư nữa)
        </button>
      )}
    </div>
  )
}
